import { ErrorNotification } from '@/components/toast/toastMessages';
import { useAppDispatch, useAppState } from '@/context/appState';
import { SelectOption } from '@/components/Input/Dropdown/Select/Select';
import { Address, ModalDataTypes } from '@/context/types';
import { useSearchParams } from 'react-router-dom';
import { ModalType } from '@/components/Modals/ModalFactory';

export interface FullAddress {
  type: string;
  geometry: {
    type: string;
    coordinates: Array<number>; // [Longitude, Latitude]
  };
  properties: {
    name: string;
    mapbox_id: string;
    feature_type: string;
    address: string;
    full_address: string;
    context: {
      street: { name: string };
      secondary_address: { name: string };
      postcode: { name: string };
      place: { name: string };
      region: { name: string; region_code: string };
      country: { name: string; country_code: string };
    };
    coordinates: {
      longitude: number;
      latitude: number;
      accuracy: string;
    };
  };
}

interface FullAddressCollection {
  type: string;
  features: Array<FullAddress>;
  attribution: string;
}

interface MapBoxSuggestion {
  name: string;
  mapbox_id: string;
  feature_type: string;
  address: string;
  full_address: string;
  place_name: string;
  context: {
    country: { name: string; country_code: string };
    region: { name: string; region_code: string };
    postcode: { name: string };
    place: { name: string };
  };
  language: string;
  maki: string;
}

const mapbox_access_token = import.meta.env.VITE_MAPBOX_TOKEN;

export const useLocalityLock = () => {
  const Locality_Lock = import.meta.env?.VITE_COTTAGE_FOOD_LOCK == 'lock';

  function isValidLocality(data: FullAddress): boolean;
  function isValidLocality(data: MapBoxSuggestion): boolean;
  function isValidLocality(data: MapBoxSuggestion['context']): boolean;
  function isValidLocality(data: Address): boolean;
  function isValidLocality(data: any): boolean {
    let region_code: string;
    let country_code: string;

    // 1. Check for FullAddress structure
    if ('properties' in data) {
      region_code = data?.properties?.context?.region.region_code;
      country_code = data?.properties?.context?.country?.country_code;
    }
    // 2. Check for MapBoxSuggestion structure
    else if ('context' in data) {
      region_code = data?.context?.region?.region_code;
      country_code = data?.context?.country?.country_code;
    }
    // Check for MapBox[context]
    else if ('country' in data && data?.country?.name) {
      region_code = data?.region?.region_code;
      country_code = data?.country?.country_code;
    }
    // 3. Fallback to Address structure
    else {
      region_code = data?.region_code;
      country_code = data?.country_code;
    }

    if (Locality_Lock) {
      return region_code === 'TX' && country_code === 'US';
    }
    return country_code === 'US';
  }

  const LocalitySelectLock: any = Locality_Lock
    ? {
        isLocked: true,
        lockMessage: 'In-State Deliveries Only!',
        defaultValue: { label: 'Texas', value: 'TX' },
      }
    : {};

  const texasBBox = '-106.645646,25.837377,-93.508292,36.500704';

  // Houston, TX Coordinates: -95.3698, 29.7604
  const houstonCoords = '-95.3698,29.7604';

  const localLockSuggestionUrl = (token: string, query: string) => {
    return `https://api.mapbox.com/search/searchbox/v1/suggest?q=${query}&language=en&country=us${Locality_Lock ? `&bbox=${texasBBox}&proximity=${houstonCoords}` : ''}&session_token=${token}&access_token=${mapbox_access_token}`;
  };

  const localLockRetrieveURL = (token: string, mapboxId: string) => {
    return `https://api.mapbox.com/search/searchbox/v1/retrieve/${mapboxId}?session_token=${token}&access_token=${mapbox_access_token}`;
  };

  return {
    isValidLocality,
    LocalitySelectLock,
    localLockRetrieveURL,
    localLockSuggestionUrl,
  };
};

/**
 * Must be signed in to use
 */
export const useAddressSuggestions = () => {
  const dispatch = useAppDispatch();
  const state = useAppState();
  const { sessionToken } = state.mapbox;
  const { isValidLocality, localLockSuggestionUrl } = useLocalityLock();

  const getSuggestions = async (query: string): Promise<SelectOption[]> => {
    let scopedToken = sessionToken;
    if (!scopedToken) {
      scopedToken = window.crypto.randomUUID();
      dispatch({ type: 'GEN_MAP_SESSION', token: scopedToken });
    }
    if (query.length > 2 && sessionToken) {
      const url = localLockSuggestionUrl(scopedToken, query);

      try {
        const response = await fetch(url);
        const data = await response.json();
        return data.suggestions.reduce(
          (
            list: SelectOption[],
            {
              full_address,
              mapbox_id,
              feature_type,
              context,
            }: MapBoxSuggestion,
          ) => {
            if (
              (feature_type === 'address' || feature_type === 'street') &&
              isValidLocality(context)
            ) {
              list.push({ label: full_address, value: mapbox_id });
            }
            return list;
          },
          [],
        );
      } catch (error) {
        ErrorNotification({
          title: 'Autocomplete Unavailable',
          message:
            'We’re having trouble finding suggestions. Please type your address manually.',
          theme: 'tinted', // Uses the semi-transparent semantic background
        });
        console.error('Mapbox Suggest Error:', error);
      }
    }
    return [];
  };

  return { getSuggestions };
};

export default ({
  onAddressConfirmed,
}: {
  onAddressConfirmed?: ModalDataTypes['AddressConfirm']['onAddressConfirmed'];
} = {}) => {
  const {
    mapbox: { sessionToken },
    user,
  } = useAppState();
  const { fullName = '' } = user ?? {};
  const dispatch = useAppDispatch();
  const { localLockRetrieveURL } = useLocalityLock();

  async function selectAddress({ value: mapboxId }: SelectOption) {
    const url = localLockRetrieveURL(sessionToken!, mapboxId as string);

    try {
      const response = await fetch(url);
      const data: FullAddressCollection = await response.json();
      const {
        properties: {
          mapbox_id,
          full_address,
          context: {
            street: { name: street },
            secondary_address: { name: secondary_address = '' } = {},
            postcode: { name: postcode },
            place: { name: place },
            region: { name: region, region_code },
            country: { name: country, country_code },
          },
        },
      } = data.features[0];

      dispatch({
        type: 'OPEN_ADDRESS_CONFIRM',
        fullName,
        address: {
          street,
          secondary_address,
          postcode,
          place,
          region,
          region_code,
          country: 'United States',
          country_code: 'US',
          value: mapbox_id,
          label: full_address,
        },
        instructions: '',
        onAddressConfirmed,
      });
    } catch (error) {
      ErrorNotification({
        title: 'Address Not Found',
        message:
          "We couldn't pinpoint that location. Please enter your details manually.",
        theme: 'tinted', // Matches the design from toasts.webp
      });
      console.error('Mapbox Retrieve Error:', error);
    }
  }

  const openManualAddress = () => {
    dispatch({
      type: 'OPEN_ADDRESS_CONFIRM',
      fullName,
      address: {
        street: '',
        secondary_address: '',
        postcode: '',
        place: '',
        region: '',
        region_code: '',
        country: 'United States',
        country_code: 'US',
        value: '',
        label: '',
      },
      instructions: '',
      onAddressConfirmed,
      hideSave: true,
    });
  };

  return { selectAddress, openManualAddress };
};
